var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    AccelerateSupport           = require("enyo-ui/AccelerateSupport"),
    Control                     = require("enyo/Control"),
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    Repeater,
    kind                        = require("enyo/kind"),
    utils                       = require("enyo/utils");

require("enyo-ui");

/**
 * [Repeater]{@link enyo-ui.Repeater} provides the infrastructure to create,
 * render and arrange child controls for each record in a dataset.
 *
 * It needs an adapter for the particular type of data it will receive - such as
 * [RepeaterArraySupport]{@link enyo-ui.RepeaterArraySupport}, for array data -
 * and a [RepeaterArranger]{@link enyo-ui.RepeaterArranger} to arrange the
 * children.
 *
 * @ui
 * @class   enyo-ui.Repeater
 * @extends enyo.Control
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @mixes   enyo-ui.AccelerateSupport
 * @public
 */
Repeater = module.exports = kind(/** @lends enyo-ui.Repeater.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.Repeater",

    /**
     * Arrangers may alter width and height of the repeater when updating the
     * size of the arrangement.
     *
     * @name style
     * @type {String}
     * @default ''
     * @memberof enyo-ui.FadeSupport
     * @public
     */

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport,
        AccelerateSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-repeater",

    /**
     * The kind responsible for arranging children within the
     * childContainer.
     *
     * @type {enyo-ui.RepeaterArranger}
     * @public
     */
    arrangerKind: null,

    /**
     * Options for the arranger.
     *
     * @type {Object=}
     * @public
     */
    arrangerOptions: null,

    /**
     * The kind responsible for implementing the way the repeater interacts
     * with it's dataset.
     *
     * @type {enyo-ui.RepeaterDataAdapter}
     * @public
     */
    dataAdapterKind: null,

    /**
     * The DataAdapter should respect this setting and reflow (or not) before
     * drawing children.
     *
     * @private
     */
    reflowOnDataChange: false,

    /**
     * The kind that will be rendered for each item in the dataset.
     *
     * @type {enyo.Control}
     * @public
     */
    childKind: null,

    /**
     * Optional extra options to merge into the child kind at creation time.
     *
     * @type {Object=}
     * @public
     */
    childOptions: null,

    /**
     * Define a selector for the component that will serve as container for
     * the repeater children.
     *
     * @type    {string}
     * @default ".$.childContainer"
     * @public
     */
    childContainerPath: ".$.childContainer",

    /**
     * Whether the drawing process should be asynchronous, drawing one child
     * at at time.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    drawAsync: false,

    /**
     * The dataset feeding this repeater.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * The length of the dataset.
     *
     * @type {number}
     * @readonly
     * @public
     */
    length: 0,

    /**
     * This hash maps the unique ids of records in the dataset to child
     * controls. This mapping will be updated any time a child is created or
     * removed.
     *
     * @type {Object}
     * @private
     */
    recordUidToChild: null,

    /**
     * This hash maps currently arranged indexes to their child controls.
     *
     * @type {Object}
     * @private
     */
    indexToChild: null,

    /**
     * An array of indexes that need to be drawn.
     *
     * @type {Array}
     * @private
     */
    undrawnIndexes: null,

    /**
     * A flag that tells us if the repeater is currently drawing its children.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    drawing: false,

    /**
     * The timeout that will trigger the drawing the next repeater child.
     *
     * @type {timeoutID}
     * @private
     */
    drawTimeout: null,

    /**
     * An object storing the size of the arrangement. The exact value will be
     * determined by the arranger but it'll likely contain at least a width or a
     * height value.
     *
     * @type {Object}
     * @private
     */
    arrangementSize: {
        width:  null,
        height: null
    },

    /**
     * An object storing the size of the childContainer. The exact value will be
     * determined by the `scaleChildContainer` method, but it'll likely contain
     * at least a width or a height value.
     *
     * @type {Object}
     * @private
     */
    childContainerSize: {
        width:  null,
        height: null
    },

    observers: {
        scaleChildContainer: ["arrangementSize"]
    },

    /**
     * @private
     */
    handlers: {
        onpostresize: "handlePostResize"
    },

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.recordUidToChild   = {};
            this.indexToChild       = {};
            this.undrawnIndexes     = [];
            this.arrangementSize    = utils.clone(this.arrangementSize);
            this.childContainerSize = utils.clone(this.childContainerSize);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            // Create components
            sup.apply(this, arguments);

            // Ensure a container for the repeater children exists
            if (!this.getChildContainer()) {
                this.createComponent({
                    name:    "childContainer",
                    classes: "enyo-ui-repeater__child-container"
                });
            }
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.drawNext = this.bindSafely(this.drawNext);
            sup.apply(this, arguments);
            this.createArranger();
            this.createDataAdapter();
        };
    }),

    /**
     * Creates an arranger instance responsible for arranging the children. All
     * repeater children will be arranged within the bounds of the
     * childContainer by this arranger.
     *
     * @private
     */
    createArranger: function() {
        if (!this.arrangerKind) {
            this.error("Arranger required.");
        }
        this.arranger = kind.createFromKind(this.arrangerKind, this);
    },

    /**
     * Returns the arranger.
     *
     * @returns  {?Component}  The arranger, if available.
     * @public
     */
    getArranger: function() {
        return this.arranger || null;
    },

    /**
     * Creates an adapter for handling the dataset passed to this repeater.
     *
     * @private
     */
    createDataAdapter: function() {
        if (!this.dataAdapterKind) {
            this.error("Data Adapter required.");
        }
        this.dataAdapter = kind.createFromKind(this.dataAdapterKind, this);
    },

    /**
     * Returns the data adapter.
     *
     * @returns  {?Component}  The data adapter, if available.
     * @public
     */
    getDataAdapter: function() {
        return this.dataAdapter || null;
    },

    // Destruction
    // --------------------------------

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            var arranger    = this.arranger,
                dataAdapter = this.dataAdapter;

            arranger && arranger.destroy && arranger.destroy();
            dataAdapter && dataAdapter.destroy && dataAdapter.destroy();
            sup.apply(this, arguments);
        };
    }),

    // Dataset
    // --------------------------------

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {number}  index  The index
     * @return {*}       The record, or `null` if not found.
     * @abstract
     * @protected
     */
    getRecordByIndex: function(index) {
        return this.dataAdapter.getRecordByIndex(index);
    },

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {*}       record  The record
     * @return {number}  The index, or `null` if not found.
     * @protected
     */
    getIndexByRecord: function(record) {
        return this.dataAdapter.getIndexByRecord(record);
    },

    /**
     * Retrieves a unique identifier from the record.
     *
     * @param  {*}       record  The record
     * @return {string}  The record uid.
     * @abstract
     * @protected
     */
    getRecordUid: function(record) {
        return this.dataAdapter.getRecordUid(record);
    },

    // Children
    // --------------------------------

    /**
     * Tries to select the childContainer based on the `childContainerPath`.
     *
     * @return {Object}  The child container.
     * @public
     */
    getChildContainer: function() {
        return this.get(this.childContainerPath);
    },

    /**
     * Creates (but doesn't render) a child control for the provided record.
     *
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created child
     * @protected
     */
    createChild: function(record) {
        var childContainer   = this.getChildContainer(),
            recordUid        = this.getRecordUid(record),
            recordUidToChild = this.recordUidToChild,
            childKind        = this.getChildKind(record),
            childOptions     = this.getChildOptions(childKind, record),
            child;

        // Child already exists
        if (recordUidToChild[recordUid]) {
            this.error("Child for record: " + recordUid + " already exists.");

            return recordUidToChild[recordUid];
        }

        // Create child configured not to render immediately
        child = childContainer.createComponent({
            name:          "repeater_child_" + recordUid,
            kind:          childKind,
            classes:       "enyo-ui-repeater__child " + (childOptions.classes || ""),
            canGenerate:   false,
            data:          record,
            repeater:      this,
            repeaterIndex: null,
            showing:       false
        }, childOptions);

        // Index child
        recordUidToChild[recordUid] = child;

        return child;
    },

    /**
     * Override this method if the returned kind needs to change based on the
     * record it'll show.
     *
     * @param  {Object=}   record The record
     * @return {Function}  The kind constructor
     * @protected
     */
    getChildKind: function() {
        return this.childKind;
    },

    /**
     * Override this method if the returned options need to change based on the
     * kind that will be used.
     *
     * @param  {Function}  kind   The childKind
     * @param  {Object=}   record The record
     * @return {?Object}   The options
     * @protected
     */
    getChildOptions: function() {
        return this.childOptions || {};
    },

    /**
     * Gets the child associated with the record at the provided index in the
     * dataset.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The child if found, `null` otherwise.
     * @public
     */
    getChildForIndex: function(index) {
        var record,
            recordUid;

        if (!this.length) {
            return null;
        }

        record    = this.getRecordByIndex(index);

        if (!record) {
            return null;
        }

        recordUid = this.getRecordUid(record);

        if (!recordUid) {
            return null;
        }

        return this.recordUidToChild[recordUid] || null;
    },

    /**
     * Gets the child currently arranged at the provided index.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The child if found, `null` otherwise.
     * @public
     */
    getChildAtIndex: function(index) {
        return this.indexToChild[index] || null;
    },

    /**
     * Makes sure the child is removed from the arrangement, no longer mapped to
     * it's record and destroyed.
     *
     * @param {Object}  record  The record
     * @protected
     */
    destroyChild: function(record) {
        var recordUid = this.getRecordUid(record),
            child     = this.recordUidToChild[recordUid],
            index;

        if (child) {
            index = child.repeaterIndex;

            if (index !== null) {
                this.unarrangeChild(child);
            }
            delete this.recordUidToChild[recordUid];
            child.destroy();
        }
    },

    /**
     * Iterates over indexed records and removes the corresponding children.
     *
     * @private
     */
    destroyChildren: function() {
        var recordUidToChild = this.recordUidToChild,
            recordUid;

        this.stopDrawing();

        for (recordUid in recordUidToChild) {
            if (recordUidToChild.hasOwnProperty(recordUid)) {
                this.destroyChild(recordUidToChild[recordUid].data);
            }
        }
        this.undrawnIndexes = [];
    },

    // Render, Resize, Reflow
    // --------------------------------

    /**
     * Interrupt drawing if needed. Remove draw from absoluteShowing queue to
     * prevent it from getting called twice ([rendered]{@link
     * enyo-ui.Repeater#rendered} will call it explicitly).
     *
     * @method
     * @private
     */
    render: kind.inherit(function(sup) {
        return function() {
            this.stopDrawing();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Children are drawn after the repeater has rendered. At this point the
     * arranger will have already decided the size of the arrangement and
     * applied it to the DOM, which is all we really need for enyo's regular
     * render flow.
     *
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.draw();
        };
    }),

    /**
     * At this point, the arranger should do the bare minimum work required to
     * update the layout flow of the rendered application. Usually this only
     * involves scaling the child container. Arranging the children within the
     * container will happen after resize or after render.
     *
     * @private
     */
    reflow: function() {
        this.arranger.reflow();
        this.updateArrangementSize();
    },

    /**
     * Updates cached arrangementSize.
     *
     * @protected
     */
    updateArrangementSize: function() {
        var currentSize,
            newSize;

        currentSize = this.get("arrangementSize") || {};
        newSize     = this.arranger.getArrangementSize();

        // Only update arrangementSize if it actually changed
        if (currentSize.width !== newSize.width
            || currentSize.height !== newSize.height) {
            this.set("arrangementSize", newSize);
        }
    },

    /**
     * Scales the childContainer to accommodate the entire arrangement.
     *
     * @protected
     */
    scaleChildContainer: function() {
        var arrangementSize = this.arrangementSize,
            childContainer  = this.getChildContainer(),
            bounds          = {},
            childContainerBounds;

        // Scaling the childContainer happens during reflow, but the repeater
        // children are redrawn later for performance reasons. Because of this,
        // child controls might extend outside of the boundaries of the
        // childContainer until they have been re-drawn.
        //
        // In order to ensure the dimensions we apply here are reliable we
        // cannot allow the childContainer to have any visible overflow.
        childContainer.applyStyle("overflow", "hidden");

        if (arrangementSize.width || arrangementSize.width === 0) {
            bounds.width = arrangementSize.width;
        }

        if (arrangementSize.height || arrangementSize.height === 0) {
            bounds.height = arrangementSize.height;
        }

        if (bounds.width !== this.childContainerSize.width
            || bounds.height !== this.childContainerSize.height) {
            childContainer.setBounds(bounds);
            childContainerBounds = childContainer.getBounds();
            this.set("childContainerSize", {
                width:  childContainerBounds.width,
                height: childContainerBounds.height
            });
        }
    },

    /**
     * Start draw cycle after resizing.
     *
     * @private
     */
    handlePostResize: function() {
        this.draw();
    },

    // Draw
    // --------------------------------

    /**
     * Marks the start of a draw cycle. Internal use only.
     *
     * @private
     */
    startDrawing: function() {
        if (!this.drawing) {
            this.set("drawing", true);
            this.set("needsDraw", false);
        }
    },

    /**
     * Creates a list of indexes that need to have a child drawn.
     *
     * Drawing will be deferred if the repeater is not absolutely showing or if
     * the application is in performance mode.
     *
     * @protected
     */
    draw: function() {
        var alreadyDrawing = this.drawing,
            bounds;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("draw");

            return;
        }
        this.removeFromAbsoluteShowingQueue("draw");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("draw");

            return;
        }
        this.removeFromPerformanceModeQueue("draw");

        // Start draw loop if not started yet, unset the request for a new
        // draw call otherwise.
        !alreadyDrawing ? this.startDrawing() : this.set("needsDraw", false);

        // If there is no data, just update the arrangementSize and we're done.
        if (this.get("length") === 0) {
            this.updateArrangementSize();

            return;
        }

        // Get the bounds of the child container, these can be used to
        // determine if only a subset of children should be arranged.
        bounds = this.getArrangementBounds();

        // Get indexes that should be arranged.
        this.undrawnIndexes = this.getArrangementIndexes(bounds).reverse();

        // Nothing to draw, stop drawing
        if (this.undrawnIndexes.length === 0) {
            this.stopDrawing();

            return;
        }

        // Start drawing children for individual indexes.
        this.drawAsync ? this.requestAsyncDrawNext() : this.drawNext();
    },

    /**
     * Stops any currently active draw loop.
     *
     * @public
     */
    stopDrawing: function() {
        if (this.drawing) {
            this.removeFromAbsoluteShowingQueue("draw");
            this.removeFromPerformanceModeQueue("draw");
            this.removeFromAbsoluteShowingQueue("drawNext");
            this.removeFromPerformanceModeQueue("drawNext");
            this.cancelAsyncDrawNext();
            this.set("drawing", false);
        }
    },

    /**
     * Continues any previously interrupted draw loop.
     *
     * @public
     */
    continueDrawing: function() {
        if (this.undrawnIndexes.length > 0) {
            this.set("drawing", true);
            this.drawAsync ? this.requestAsyncDrawNext() : this.drawNext();
        }
    },

    /**
     * Evaluates if there are any undrawn indexes. If so, an index is popped off
     * the stack and a child control created and rendered as needed.
     *
     * This method will be deferred if the repeater is not absolutely showing or
     * if the application is in performance mode.
     *
     * @protected
     */
    drawNext: function() {
        var drawAsync      = this.drawAsync,
            undrawnIndexes = this.undrawnIndexes,
            index,
            record,
            child;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("drawNext");

            return;
        }
        this.removeFromAbsoluteShowingQueue("drawNext");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("drawNext");

            return;
        }
        this.removeFromPerformanceModeQueue("drawNext");

        if (this.drawTimeout) {
            this.drawTimeout = null;
        }

        // No undrawn indexes, we're done with this cycle
        if (undrawnIndexes.length === 0) {

            // Check if another cycle might be needed
            if (this.needsDraw) {
                this.draw();
            }
            else {
                this.stopDrawing();
            }

            return;
        }

        // Ensure a child for the next index
        index  = undrawnIndexes.pop();
        record = this.getRecordByIndex(index);

        // Only draw child if record is found
        if (record) {
            child = this.getChildForIndex(index);
            child = child ? this.updateChild(child, record) : this.createChild(record);
            child && this.drawChild(child);
        }

        // Update arrangement
        if (!drawAsync) {

            // Schedule a redraw if arranging the child changed the flow of the
            // arrangement.
            if (child && this.arrangeChild(child, index)) {
                this.updateArrangementSize();
                this.set("needsDraw", true);
            }
        }
        else {
            this.arrange();
        }

        // Schedule next draw
        drawAsync ? this.requestAsyncDrawNext() : this.drawNext();
    },

    /**
     * Always defer drawing in PerformanceMode. Override if another strategy is
     * desired.
     *
     * @returns {bool} Whether to defer drawing the next child.
     * @protected
     */
    shouldDeferDrawInPerformanceMode: function() {
        return true;
    },

    /**
     * Schedules drawNext call to be executed asynchronously.
     *
     * @protected
     */
    requestAsyncDrawNext: function() {
        if (!this.drawTimeout) {
            this.drawTimeout = setTimeout(this.drawNext, 0);
        }
    },

    /**
     * Cancels the scheduled drawNext call.
     *
     * @protected
     */
    cancelAsyncDrawNext: function() {
        this.drawTimeout && clearTimeout(this.drawTimeout);
        this.drawTimeout = null;
    },

    /**
     * This method can be used to update the dataset of a child right before
     * it's drawn.
     *
     * @param  {enyo.Control}  child   The child
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created child
     * @public
     */
    updateChild: function(child, record) {
        return this.dataAdapter.updateChild(child, record);
    },

    /**
     * Draws a child.
     *
     * @param  {enyo.Control}  child  The child
     * @private
     */
    drawChild: function(child) {
        this.arranger.drawChild(child);
    },

    // Arrange
    // --------------------------------

    /**
     * Arranges existing children to the correct position, this should be fast
     * and should not introduce reflows.
     *
     * Arranging will be deferred if the repeater is not absolutely showing.
     *
     * @return {boolean} `true` if the flow of the arrangement changed, `false`
     *                   otherwise.
     * @protected
     */
    arrange: function() {
        var bounds,
            arrangementIndexes,
            arranged    = 0,
            i,
            index,
            childAtIndex,
            childForIndex,
            flowChanged = false;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("arrange");

            return false;
        }
        this.removeFromAbsoluteShowingQueue("arrange");

        // Get the bounds of the child container, these can be used to determine
        // if only a subset of children should be arranged.
        bounds = this.getArrangementBounds();

        // Get sorted list of indexes that should be arranged.
        arrangementIndexes = this.getArrangementIndexes(bounds);

        // Within this loop we arrange children for all arrangementIndexes. At
        // the end of the loop, we get a new sorted list of arrangementIndexes.
        // If the new list contains new indexes, we arrange those too.
        while (arranged < arrangementIndexes.length) {

            // Only arrange indexes that weren't arranged in a previous cycle.
            arrangementIndexes = arrangementIndexes.slice(arranged);

            // Find out which children to arrange, and which to unarrange
            for (i = 0; i < arrangementIndexes.length; i++) {
                index         = arrangementIndexes[i];
                childAtIndex  = this.getChildAtIndex(index);
                childForIndex = this.getChildForIndex(index);

                // If the wrong child is currently arranged at this index,
                // unarrange it.
                if (childAtIndex && childAtIndex !== childForIndex) {
                    if (this.unarrangeChild(childAtIndex)) {
                        flowChanged = true;
                    }
                }

                // Arrange the correct child for this index.
                if (childForIndex) {
                    if (this.arrangeChild(childForIndex, index)) {
                        flowChanged = true;
                    }
                }
                else {

                    // The child for this index is missing, a draw cycle is
                    // needed.
                    this.set("needsDraw", true);
                }

                ++arranged;
            }

            // Get a new sorted list of indexes that should be arranged.
            arrangementIndexes = this.getArrangementIndexes(bounds);
        }

        // Ensure up-to-date arrangementSize.
        this.updateArrangementSize();

        return flowChanged;
    },

    /**
     * Arranges the child at the provided index. Returns whether arranging the
     * child at this index changed the flow of the arrangement as a whole.
     *
     * @param  {enyo.Control}  child  The child
     * @param  {number}        index  The index
     * @return {boolean}       `true` if flow changed, `false` otherwise
     * @public
     */
    arrangeChild: function(child, index) {
        var currentIndex = child.repeaterIndex,
            flowChanged;

        if (currentIndex !== index) {
            delete this.indexToChild[currentIndex];
        }
        flowChanged              = this.arranger.arrangeChild(child, index);
        !child.showing           && child.show();
        this.indexToChild[index] = child;

        return flowChanged;
    },

    /**
     * Unarranges the child. Returns whether arranging the child at this index
     * changed the flow of the arrangement as a whole.
     *
     * @param  {enyo.Control}  child  The child
     * @return {boolean}       `true` if flow changed, `false` otherwise
     * @public
     */
    unarrangeChild: function(child) {
        var flowChanged;

        delete this.indexToChild[child.repeaterIndex];
        child.showing && child.hide();
        flowChanged = this.arranger.unarrangeChild(child);

        return flowChanged;
    },

    /**
     * Gets the bounds of the area for which children need to be arranged. The
     * bounds should be defined in pixels, relative to the top-left corner of
     * the childContainer.
     *
     * @return {Object}  The bounds
     * @public
     */
    getArrangementBounds: function() {
        return {
            top:    0,
            left:   0,
            bottom: Infinity,
            right:  Infinity
        };
    },

    /**
     * Calculates the indexes that make up the arrangement within the provided
     * bounds.
     *
     * @param  {Object}  bounds  The bounds
     * @return {Array}   The arrangement indexes.
     * @protected
     */
    getArrangementIndexes: function(bounds) {
        return this.arranger.getArrangementIndexes(bounds);
    }

});

/**
 * This method allows enyo.kind to extend the `arrangerOptions` and
 * `childOptions` hashes when creating super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
Repeater.concat = function(ctor, props) {
    var proto   = ctor.prototype || ctor,
        options = [
            "arrangerOptions",
            "childOptions"
        ];

    options.forEach(function(prop) {
        var extension = proto[prop] ? utils.clone(proto[prop]) : {};
        proto[prop]   = utils.mixin(extension, props[prop]);
        delete props[prop];
    });
};
